import React, { useEffect, useState } from 'react';
import {
	DndContext,
	DragOverlay,
	KeyboardSensor,
	MouseSensor,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { usePrevious } from '@dnd-kit/utilities';
import { isEqual } from 'lodash';

import { useCustomCollisionDetection, useDragHandlers } from './helpers';

import { SortableItemOverlay } from './SortableItem';
import { LaneContent, SortableLane } from './Lane';
import { CardContent } from './Card';
import { SortableList } from './SortableList';

import Slider from '../Shell/Slider';
import { Button } from '../Button';
import LoadingSkeleton from '../Shell/LoadingSkeleton';
import { useSidebar } from '../../context/LayoutContext';
import { useApp } from '../../context/AppContextBeta';

function useBoardData(availableWidgets) {
	const laneState = useState(availableWidgets);
	const clonedLaneState = useState(null);

	const activeLaneState = useState(null);
	const activeItemState = useState(null);

	return {
		laneState,
		clonedLaneState,
		activeLaneState,
		activeItemState,
	};
}

const rootContext = {
	parentId: null,
	indexPath: [],
	data: {},
};

const WidgetDashboard = ({
	isLoading = true,
	availableWidgets,
	activeWidgets,
	onChange,
	widgetSettings,
}) => {
	if (isLoading) {
		return <LoadingSkeleton />;
	}

	if (!availableWidgets?.all?.length) {
		return null;
	}

	const [isWidgetSliderOpen, setWidgetSlider] = useState(false);
	const { isMobile, isTablet, isDesktop } = useApp();

	const { laneState, clonedLaneState, activeLaneState, activeItemState } =
		useBoardData(availableWidgets?.all);

	const currLaneState = laneState[0][0].children;
	const prevLaneState = usePrevious(currLaneState);

	useEffect(() => {
		if (
			typeof prevLaneState !== 'undefined' &&
			!isEqual(prevLaneState, currLaneState)
		) {
			if (typeof onChange === 'function') {
				onChange(currLaneState?.map((item) => item.id));
			}
		}
	}, [currLaneState]);

	const {
		onDragStart,
		onDragCancel,
		onDragOver,
		onDragEnd,
		onForceMove,
		onBulkMove,
	} = useDragHandlers({
		laneState,
		clonedLaneState,
		activeLaneState,
		activeItemState,
	});

	const currActiveWidgets = activeWidgets;
	const prevActiveWidgets = usePrevious(currActiveWidgets);
	useEffect(() => {
		if (!prevActiveWidgets && currActiveWidgets) {
			if (currActiveWidgets.length) {
				onBulkMove(currActiveWidgets, laneState[(0, 0)]);
			}
		}
	}, [activeWidgets]);

	const lanes = laneState[0];
	const activeLane = activeLaneState[0];
	const activeItem = activeItemState[0];

	const collisionDetection = useCustomCollisionDetection(
		laneState[0],
		activeLane,
	);

	let activeDrag = null;

	if (activeLane) {
		activeDrag = (
			<SortableItemOverlay ctx={activeLane} className="lane">
				<LaneContent
					parentId={activeLane.parentId}
					lane={activeLane.data || {}}
					indexPath={activeLane.indexPath}
					isOverlay={true}
				/>
			</SortableItemOverlay>
		);
	} else if (activeItem) {
		activeDrag = (
			<SortableItemOverlay ctx={activeItem} className="item">
				<CardContent item={activeItem.data} isPreview />
			</SortableItemOverlay>
		);
	}

	const rootContent = React.useMemo(() => {
		return {
			id: 'root',
			type: 'root',
			children: lanes,
			data: {},
		};
	}, [lanes]);

	const pointerSensor = useSensor(PointerSensor, {
		activationConstraint: {
			distance: 1,
		},
	});
	const mouseSensor = useSensor(MouseSensor);
	const touchSensor = useSensor(TouchSensor);
	const keyboardSensor = useSensor(KeyboardSensor);

	const sensors = useSensors(
		mouseSensor,
		pointerSensor,
		touchSensor,
		keyboardSensor,
	);

	return (
		<>
			{lanes[0].children.length === 0 && (
				<div className="w-1/2 p-2 transition duration-200 ease-in-out h-96">
					<div className="bg-[#f9f8f8] w-full h-full p-5 flex justify-center items-center flex-col gap-4">
						<p>Drag and drop new widgets</p>
						<Button type="secondary" onClick={() => setWidgetSlider(true)}>
							Customize
						</Button>
					</div>
				</div>
			)}

			{isDesktop && (
				<>
					{lanes[0].children.length > 0 && (
						<div className="w-full flex items-center justify-end mb-6">
							<Button type="secondary" onClick={() => setWidgetSlider(true)}>
								Customize Dashboard
							</Button>
						</div>
					)}
				</>
			)}

			<DndContext
				onDragStart={onDragStart}
				onDragCancel={onDragCancel}
				onDragOver={onDragOver}
				onDragEnd={onDragEnd}
				collisionDetection={collisionDetection}
				sensors={sensors}
			>
				<div>
					<SortableList
						className="board"
						accepts="lane"
						direction={'Horizontal'}
						content={rootContent}
						ctx={rootContext}
					>
						<div>
							<SortableLane
								key={lanes[0].id}
								parentId="root"
								lane={lanes[0]}
								laneIndex={0}
								onForceMove={onForceMove}
								widgetSettings={widgetSettings}
							/>
						</div>

						<Slider
							title="Available Widgets"
							forceOpen={isWidgetSliderOpen}
							onClose={() => setWidgetSlider(false)}
							overlay={false}
						>
							<p className="text-gray-400 font-extralight mb-3 text-sm">
								Click add or drag widgets below to your Home screen. You can
								also reorder and remove them.
							</p>

							<SortableLane
								key={lanes[1].id}
								parentId="root"
								lane={lanes[1]}
								laneIndex={1}
								defaultPreview={true}
								destinationLane={lanes[0]}
								onForceMove={onForceMove}
							/>
						</Slider>
					</SortableList>

					{createPortal(<DragOverlay>{activeDrag}</DragOverlay>, document.body)}
				</div>
			</DndContext>
		</>
	);
};

export default WidgetDashboard;
